import { ensureError } from '@hover/utilities';
import { useParams } from 'react-router';
import type { AnyObject, InferType, ObjectSchema } from 'yup';
import { ValidationError } from 'yup';

class ParseParamsError extends Error {
  failure: ValidationError | null = null;

  constructor(error: Error) {
    super(error.message);

    // I believe this is only necessary when we're transpiling to ES5. It should
    // be possible to sub-class built-ins with native ES6 class syntax, but this
    // is required for ES5 (otherwise `instanceof` won't work as expected).
    Object.setPrototypeOf(this, ParseParamsError.prototype);

    if (error instanceof ValidationError) {
      this.failure = error;
    }

    this.name = 'ParseParamsError';
  }
}

type ParamParseFailure = Record<string, undefined> & {
  __error: Error;
};

const parseParams = <
  TSchema extends ObjectSchema<{}, AnyObject>,
  TStrict extends boolean = false,
>(
  params: Record<string, unknown>,
  schema: TSchema,
  strict?: TStrict,
): TStrict extends true
  ? InferType<TSchema>
  : InferType<TSchema> | ParamParseFailure => {
  try {
    return schema.validateSync(params);
  } catch (caught) {
    const error = new ParseParamsError(ensureError(caught));

    if (strict) throw error;

    return { __error: error };
  }
};

const useParsedParams = <
  TSchema extends ObjectSchema<{}, AnyObject>,
  TStrict extends boolean = false,
>(
  schema: TSchema,
  strict?: TStrict,
): TStrict extends true
  ? InferType<TSchema>
  : InferType<TSchema> | ParamParseFailure => {
  const params = useParams();

  return parseParams(params, schema, strict);
};

export { useParsedParams, parseParams, ParseParamsError };
